// Eigentümerportal (Portale E7) im Rechnungs-Detail: Wartet die Rechnung auf den Eigentümer (über seiner
// Wertgrenze) oder hat er entschieden? Bei Ablehnung ist „bezahlt“ in der Datenbank gesperrt, bis das Büro die
// Entscheidung zurücksetzt. Es wird nichts verschickt.
import { useCallback, useEffect, useState } from 'react';
import { ArrowCounterClockwise, CheckCircle, HourglassMedium, XCircle } from '@phosphor-icons/react';
import { supabase } from '../../lib/supabase/client';
import { addTimelineEvent } from '../../lib/rechnungen/timeline';
import { entscheidungsLage } from '../../lib/portal/eigentuemerE7';

interface Stand {
  status: string | null; brutto: number | null; eigentuemer_entscheidung: 'freigegeben' | 'abgelehnt' | null;
  eigentuemer_entschieden_am: string | null; eigentuemer_begruendung: string | null;
  eigentuemer: { freigabe_wertgrenze: number | null } | null;
}

const DATUM = (iso: string) => new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const EURO = (n: number) => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

export default function EigentuemerEntscheidung({ rechnungId, userId, melde, bestaetige, onGeaendert }: {
  rechnungId: string;
  userId: string;
  melde: (text: string, fehler?: boolean) => void;
  bestaetige: (r: { nachricht: string; label: string; gefahr?: boolean }) => Promise<boolean>;
  onGeaendert?: () => void;
}) {
  const [stand, setStand] = useState<Stand | null>(null);
  const laden = useCallback(async () => {
    const { data } = await supabase.from('rechnungen')
      .select('status, brutto, eigentuemer_entscheidung, eigentuemer_entschieden_am, eigentuemer_begruendung, eigentuemer:eigentuemer_id(freigabe_wertgrenze)')
      .eq('id', rechnungId).maybeSingle();
    setStand((data as unknown as Stand | null) ?? null);
  }, [rechnungId]);
  useEffect(() => { void laden(); }, [laden]);

  if (!stand) return null;
  const grenze = stand.eigentuemer?.freigabe_wertgrenze ?? null;
  const lage = entscheidungsLage({ ...stand, grenze });
  if (!lage) return null;

  const zuruecksetzen = async () => {
    if (!(await bestaetige({
      nachricht: 'Entscheidung des Eigentümers zurücksetzen?\n\nDie Rechnung wartet danach wieder auf seine Freigabe im Eigentümerportal (falls sie über seiner Wertgrenze liegt).',
      label: 'Zurücksetzen', gefahr: false,
    }))) return;
    const { error } = await supabase.from('rechnungen').update({
      eigentuemer_entscheidung: null, eigentuemer_entschieden_am: null, eigentuemer_entschieden_von: null, eigentuemer_begruendung: null,
    }).eq('id', rechnungId);
    if (error) { melde('Zurücksetzen fehlgeschlagen: ' + error.message, true); return; }
    await addTimelineEvent(rechnungId, 'status_aenderung', userId, 'Entscheidung des Eigentümers zurückgesetzt').catch(() => undefined);
    melde('Entscheidung zurückgesetzt');
    await laden();
    onGeaendert?.();
  };

  const stil = {
    wartet: { farbe: 'var(--warn)', flaeche: 'var(--warn-soft)', icon: HourglassMedium, titel: 'Wartet auf Freigabe durch den Eigentümer' },
    freigegeben: { farbe: 'var(--ok)', flaeche: 'var(--ok-soft)', icon: CheckCircle, titel: 'Vom Eigentümer freigegeben' },
    abgelehnt: { farbe: 'var(--bad)', flaeche: 'var(--bad-soft)', icon: XCircle, titel: 'Vom Eigentümer abgelehnt' },
  }[lage];
  const I = stil.icon;
  const text = lage === 'wartet'
    ? `Betrag über seiner Wertgrenze von ${EURO(Number(grenze))}. Der Eigentümer sieht die Rechnung im Eigentümerportal, sofern es für ihn freigeschaltet ist.`
    : lage === 'abgelehnt'
      ? `${stand.eigentuemer_entschieden_am ? `Am ${DATUM(stand.eigentuemer_entschieden_am)}: ` : ''}„${stand.eigentuemer_begruendung ?? ''}“ – als bezahlt markieren ist gesperrt, bis die Entscheidung zurückgesetzt ist.`
      : stand.eigentuemer_entschieden_am ? `Am ${DATUM(stand.eigentuemer_entschieden_am)} im Eigentümerportal.` : 'Im Eigentümerportal.';

  return (
    <section className="immo-glas flex flex-col" style={{ borderRadius: 24, padding: '14px 16px', gap: 8, boxShadow: `inset 0 0 0 1.5px ${stil.farbe}` }} aria-label={stil.titel}>
      <div className="flex items-center" style={{ gap: 10 }}>
        <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 30, height: 30, borderRadius: 10, background: stil.flaeche, color: stil.farbe }}>
          <I size={17} weight="fill" />
        </span>
        <span className="flex-1" style={{ fontSize: 15, fontWeight: 800 }}>{stil.titel}</span>
        {lage !== 'wartet' && (
          <button type="button" onClick={() => void zuruecksetzen()} data-tip="Entscheidung zurücksetzen – die Rechnung wartet dann wieder auf den Eigentümer"
            className="inline-flex items-center" style={{ height: 34, padding: '0 12px', gap: 6, borderRadius: 17, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, background: 'var(--fill2)', color: 'var(--ink)' }}>
            <ArrowCounterClockwise size={14} weight="bold" />Zurücksetzen
          </button>
        )}
      </div>
      <span style={{ fontSize: 13, lineHeight: 1.45, color: 'var(--ink2)', overflowWrap: 'anywhere' }}>{text}</span>
    </section>
  );
}
